import { XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { Link } from 'react-router'
import * as sharedStyles from '../../components/drawers/add-findings-drawer.css'
import { Routes } from '../../routes.ts'
import { useGetRepository, useGetRepositoryScorecards } from '../../utils/api-client/api-hooks.ts'
import { type ScorecardsResponse } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { formatPercentage, joinMetaSegments } from '../../utils/text-utils.ts'
import { threatModelViewSearch } from '../../utils/threat-model/threat-model-view-param.ts'
import { RepoRiskDonut } from './repo-risk-donut.tsx'
import * as styles from './repository-risk-drawer.css.ts'
import { CONFIRMED_FINDINGS_CAVEAT, weakestDimensionsFirst } from './risk-from-scorecard.ts'
import { useRepositoryRiskScore } from './use-repository-risk-scores.ts'

/**
 * How many weak dimensions the drawer names before it stops.
 *
 * Five of the ten, not all of them: the repository's own Scorecard tab renders the full rubric properly,
 * and repeating it here would make the drawer a worse copy of a page one link away. Naming the half
 * holding the score down is what this view is for.
 */
const WEAKEST_DIMENSIONS_SHOWN = 5

/**
 * Straight to the Scorecard sub-tab, not the threat model's default view.
 *
 * `createPath` takes the tab but not the view beneath it, so the sub-tab param is appended — safe here
 * because passing `tab` guarantees the `?` this hangs off.
 */
const fullScorecardPath = (repositoryId: string): string =>
  `${Routes.RepositoryDetailsPage.createPath({ repositoryId, tab: 'threat-model' })}&${threatModelViewSearch('scorecards')}`

type RepositoryRiskDrawerPureProps = {
  repositoryId: string
  repositoryName: string
  riskScore: number | undefined
  scorecards: ScorecardsResponse
  onCloseDrawer: () => unknown
}

/**
 * Explains one repository's risk score.
 *
 * **Mounts its own query rather than being handed the row.** Query data reaching a component from its own
 * impure counterpart is what `.claude/rules/pure-impure-component-separation.md` asks for, and it is what
 * the other drawers on this page already do — `AddFindingsDrawer` and `WorkflowDrawer` both take a
 * `repositoryId`.
 *
 * The scorecard costs nothing — the row's own cell already holds that key — but `useGetRepository` is one
 * genuinely new `GET /repositories/:id`, keyed per repository where the table's key is the paginated list.
 * A cheap request for a name the row already had, accepted rather than plumbing the row through, which is
 * what the Pure/Impure rule above is asking us not to do.
 */
export const RepositoryRiskDrawer: React.FC<{ repositoryId: string; onCloseDrawer: () => unknown }> = ({
  repositoryId,
  onCloseDrawer,
}) => {
  const { data: scorecards } = useGetRepositoryScorecards({ repositoryId })
  const { data: repository } = useGetRepository({ repositoryId })
  const { riskScore } = useRepositoryRiskScore(repositoryId)

  // Both, not just the scorecard: gated on the scorecard alone, a drawer whose scorecard resolved first
  // opened with an empty title until the name arrived. Two requests race and the reader sees the loser.
  return scorecards && repository ? (
    <RepositoryRiskDrawerPure
      repositoryId={repositoryId}
      repositoryName={repository.full_name ?? repository.name}
      riskScore={riskScore}
      scorecards={scorecards}
      onCloseDrawer={onCloseDrawer}
    />
  ) : undefined
}

/**
 * Every figure here comes off the repository's threat-model scorecard: the assessment is its
 * `systemOverview`, the listed dimensions are its lowest-scoring ones, and the timestamp is when the run
 * produced them. Nothing is asserted that the scorecard does not already say.
 *
 * Pure so the drawer can be exercised against a scorecard directly, in tests and in stories, without a
 * query client deciding what a repository's posture is.
 */
export const RepositoryRiskDrawerPure: React.FC<RepositoryRiskDrawerPureProps> = ({
  repositoryId,
  repositoryName,
  riskScore,
  scorecards,
  onCloseDrawer,
}) => {
  const scorecard = scorecards.scorecard
  const weakest = scorecard ? weakestDimensionsFirst(scorecard.dimensions, WEAKEST_DIMENSIONS_SHOWN) : []

  return (
    <Dialog.Root
      open={true}
      modal={false}
      onOpenChange={open => {
        if (!open) {
          onCloseDrawer()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Content className={sharedStyles.content} onInteractOutside={event => event.preventDefault()}>
          {/* Escape already closes the drawer — Radix listens for it on the document — but nothing said so
              without a visible affordance, which every other informational drawer here provides. */}
          <Dialog.Close asChild>
            <button type="button" className={styles.closeButton} onClick={onCloseDrawer} aria-label="Close">
              <XIcon size={24} weight="light" />
            </button>
          </Dialog.Close>

          <Dialog.Title className={sharedStyles.title}>
            <span className={styles.header}>
              {riskScore !== undefined && <RepoRiskDonut riskScore={riskScore} size="48px" />}
              {repositoryName}
            </span>
          </Dialog.Title>
          {/* Provenance shares the description's line, pushed right: it qualifies the title, and on its
              own line it read as an unlabelled first section of the body. */}
          <div className={styles.headerRow}>
            <Dialog.Description className={sharedStyles.description}>
              How this repository&apos;s threat-model scorecard produces its risk score
            </Dialog.Description>
            {scorecard ? (
              <span className={styles.headerMeta}>
                {joinMetaSegments([
                  scorecard.generatedAt
                    ? `Generated ${formatDate({ date: scorecard.generatedAt, type: 'mm/dd' })}`
                    : undefined,
                  scorecard.commitSha.slice(0, 7),
                ])}
              </span>
            ) : undefined}
          </div>

          <div className={styles.body}>
            {scorecard ? (
              <section className={styles.section}>
                <p className={styles.sectionTitle}>Assessment</p>
                <p className={styles.prose}>{scorecard.systemOverview}</p>
                {/* Said where the number is explained rather than as an empty section of its own: the
                    score is genuinely incomplete, and a reader comparing two repositories deserves to
                    know confirmed findings are not in it yet rather than infer it from a blank. */}
                <p className={styles.note}>
                  <span className={styles.noteLabel}>Note: </span>
                  The score is the scorecard&apos;s ten dimensions weighted by internet-facing exposure.{' '}
                  {CONFIRMED_FINDINGS_CAVEAT} — they arrive with the scored API.
                </p>
              </section>
            ) : undefined}

            {weakest.length > 0 && (
              <section className={styles.section}>
                <p className={styles.sectionTitle}>Weakest dimensions</p>
                <ul className={styles.factorList}>
                  {weakest.map((dimension, index) => (
                    // Index in the key, not the dimension id alone: a payload repeating an id is
                    // malformed, but duplicate React keys re-render the tree rather than degrading.
                    <li key={`${dimension.dimension}-${index}`} className={styles.factor}>
                      <p className={styles.factorLabel}>
                        {dimension.displayName} · {formatPercentage(dimension.score)}
                      </p>
                      <p className={styles.factorDetail}>{dimension.rationale}</p>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className={styles.links}>
              {/* The full rubric lives one link away, which is why this drawer names only the weakest few
                  rather than reproducing all ten badly. */}
              <Link className={styles.link} to={fullScorecardPath(repositoryId)}>
                View the full scorecard
              </Link>
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
